// Single source of truth for the words in ChapterCommittees (portal DOM) and
// ChapterCommitteesPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF chapter committees";

export const cover = {
  eyebrow: "Admin · 07",
  number: "07",
  title: "Chapter committees",
  summary: "Optional scaffolding for a growing chapter. The committees other chapters have found useful, and how to run them on Slack.",
  accent: "aqua",
};

export const blocks = [
  {
    type: "lede",
    text: "Committees are optional scaffolding for a growing chapter. Once you have more than a handful of LPs, splitting the work into small standing groups keeps momentum up and pulls more people into ownership.",
  },
  { type: "heading", level: 1, text: "It's your call" },
  {
    type: "p",
    runs: [
      "Number, shape, and scope of committees is entirely at the Chapter Director's discretion. A chapter of six probably doesn't need a single one. A chapter of twenty usually wants three or four. Stand them up when the work calls for it. Don't create four empty committees on launch day and watch them gather dust.",
    ],
  },
  {
    type: "p",
    runs: [
      "Treat this doc as inspiration, not a blueprint. The committees below are the ones other chapters have found useful. Steal the ones that fit, skip the ones that don't, and invent your own when your chapter needs something these don't cover.",
    ],
  },
  { type: "heading", level: 1, text: "Common committees" },
  { type: "heading", level: 2, text: "Selection committee" },
  {
    type: "p",
    runs: [
      "A group of LPs dedicated to the actual review and selection of pitches: reading applications, scoring in the portal, and shaping the finalist list the full chapter votes on at the quarterly meeting. Useful when your application volume has grown past the point where every LP can read every pitch carefully.",
    ],
  },
  { type: "heading", level: 2, text: "Governance committee" },
  {
    type: "p",
    runs: [
      "The group that handles leadership-type decisions: anything that doesn't cleanly fall under \"pick a winner\" or \"throw an event.\" A few examples of what tends to land here:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: ["Requests to sponsor other organizations' events."] },
      {
        runs: [
          "Approving unusual uses of funds, like replacement checks, stipends, or a bigger-than-usual event budget.",
        ],
      },
      {
        runs: [
          "Chapter-level policy questions and anything sensitive enough to want more than one set of eyes on.",
        ],
      },
    ],
  },
  { type: "heading", level: 2, text: "Events committee", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Plans Good Neighbor Fun events. We highly encourage chapters to host them: on brand, on mission, and genuinely fun. Same committee usually handles co-hosting opportunities with aligned local orgs. If your chapter is looking for a low-stakes way to activate newer LPs, this is often the best on-ramp.",
    ],
  },
  { type: "heading", level: 2, text: "Founder support committee" },
  {
    type: "p",
    runs: [
      "The LPs who go out to meet the awardees. Present the check in person, sit down with the founder, hear their actual needs and goals, and bring that back to the rest of the chapter afterward. This is where the \"neighbor\" in Good Neighbor Fund gets real. The relationship with a grantee usually outlasts the grant.",
    ],
  },
  {
    type: "callout",
    label: "Start with one",
    runs: [
      "You don't have to launch all four at once. Most chapters start with whichever committee solves their most immediate bottleneck (usually Selection or Events) and add others as the chapter grows into them.",
    ],
  },
  { type: "heading", level: 1, text: "Running committees on Slack" },
  {
    type: "p",
    runs: [
      "Use the national GNF Slack to spin up a dedicated channel per committee. Keeps conversation searchable, pulls the right people in, and leaves a paper trail the next committee lead can pick up.",
    ],
  },
  { type: "heading", level: 2, text: "Recommended channel naming" },
  {
    type: "p",
    runs: [
      "Prefix every channel with your chapter abbreviation so they're discoverable across the shared workspace. For a chapter like Western New York (",
      { code: "wny" },
      "):",
    ],
    pdfRuns: [
      "Prefix every channel with your chapter abbreviation so they're discoverable across the shared workspace. For Western New York (wny):",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ code: "#wny-selection-committee" }] },
      { runs: [{ code: "#wny-governance-committee" }] },
      { runs: [{ code: "#wny-events-committee" }] },
      { runs: [{ code: "#wny-founder-support" }] },
    ],
  },
  {
    type: "p",
    runs: [
      "Swap the prefix for yours: ",
      { code: "#denver-events-committee" },
      ", ",
      { code: "#capital-region-selection-committee" },
      ", and so on. Keep the suffix consistent across chapters so LPs moving between workspaces aren't relearning conventions.",
    ],
  },
  {
    type: "heading",
    level: 2,
    text: "Keep committee channels private",
    pdfText: "Keep committee channels private",
    pdfLevel: 1,
    pdfBreak: true,
  },
  {
    type: "p",
    runs: [
      "All chapters share the same Slack workspace, so default every committee channel to ",
      { b: "private" },
      " and invite only the LPs who need to be in it. A public channel here isn't public to your chapter. It's public to every chapter, and committee conversation (pitches mid-review, funding decisions, founder details) isn't meant for that audience. Private by default, add members explicitly, remove them when they rotate off.",
    ],
    pdfRuns: [
      "All chapters share the same Slack workspace, so default every committee channel to private and invite only the LPs who need to be in it. A public channel here isn't public to your chapter. It's public to every chapter, and committee conversation (pitches mid-review, funding decisions, founder details) isn't meant for that audience. Private by default, add members explicitly, remove them when they rotate off.",
    ],
  },
  { type: "heading", level: 1, text: "Running a committee well" },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Give each committee a named lead." },
          " Someone owns the channel, calls the meeting, and brings decisions back to the full chapter.",
        ],
      },
      {
        runs: [
          { b: "Meet as often as the work requires." },
          " Not more. A dormant committee that meets anyway will kill enthusiasm faster than no committee at all.",
        ],
      },
      {
        runs: [
          { b: "Report up." },
          " Every committee should surface its decisions and open questions to the full chapter at the quarterly meeting. Otherwise you've just built silos.",
        ],
      },
      {
        runs: [
          { b: "Rotate membership." },
          " A year on a committee is plenty. Bringing fresh LPs in keeps the work from calcifying and gives newer LPs a clear path to leadership.",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "The point of committees",
    runs: [
      "Committees exist to give more LPs real ownership of the chapter, not to manufacture meetings. If a committee isn't making the chapter better, retire it. Tell its members they did good work. That's a healthy chapter, not a failed committee.",
    ],
  },
];
